import http from './http'

/** sessionStorage key: pending org invitation UUID from email link */
export const PENDING_INVITATION_STORAGE_KEY = 'noovox_b2b_invitation_id'

/**
 * Authentication Service
 * Handles all authentication-related API calls
 *
 * Uses real Supabase authentication via the backend
 */

export interface LoginRequest {
  email: string
  password: string
  invitationId?: string | null
}

export interface RegisterRequest {
  email: string
  password: string
  confirmPassword?: string
  /** Pending B2B invite UUID from ?invitation= link */
  invitationId?: string | null
}

export interface RefreshTokenRequest {
  refresh_token: string
}

export interface ForgotPasswordRequest {
  email: string
}

export interface AuthResponse {
  access_token: string
  refresh_token?: string
  user: {
    id: string
    email: string
    name?: string
    role?: string
    organization_id?: string
    first_name?: string
    last_name?: string
    job_title?: string
    linkedin_connected?: boolean
  }
  message?: string
}

export interface UserInfo {
  id: string
  email: string
  name?: string
  role?: string
  organization_id?: string
  first_name?: string
  last_name?: string
  job_title?: string
  linkedin_connected?: boolean
  avatar_url?: string | null
}

class AuthService {
  /**
   * Login user
   * POST /api/auth/login
   */
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    console.log('[AuthService.login] Authenticating:', credentials.email)
    try {
      const body: Record<string, string> = {
        email: credentials.email,
        password: credentials.password
      }
      if (credentials.invitationId) {
        body.invitation_id = credentials.invitationId
      }
      const response = await http.post<AuthResponse>('/api/auth/login', body, {
        timeout: 30000 // 30 seconds timeout
      })
      console.log('[AuthService.login] Login successful')
      return response.data
    } catch (error: any) {
      console.error('[AuthService.login] Login failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Register new user
   * POST /api/auth/register
   * 
   * NOTE: Backend expects confirm_password field for validation.
   * Frontend ignores returned tokens - user must sign in separately.
   */
  async register(userData: RegisterRequest): Promise<AuthResponse> {
    console.log('[AuthService.register] Registering:', userData.email)
    try {
      const body: Record<string, string> = {
        email: userData.email,
        password: userData.password,
        confirm_password: userData.confirmPassword ?? userData.password
      }
      if (userData.invitationId) {
        body.invitation_id = userData.invitationId
      }
      const response = await http.post<AuthResponse>('/api/auth/register', body)
      console.log('[AuthService.register] Registration successful')
      return response.data
    } catch (error: any) {
      console.error('[AuthService.register] Registration failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Refresh access token
   * POST /api/auth/refresh
   */
  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    try {
      const response = await http.post<AuthResponse>('/api/auth/refresh', {
        refresh_token: refreshToken
      })
      return response.data
    } catch (error: any) {
      throw this.handleError(error)
    }
  }

  /**
   * Logout user
   * POST /api/auth/logout
   */
  async logout(): Promise<void> {
    console.log('[AuthService.logout] Logging out')
    try {
      await http.post('/api/auth/logout')
    } catch (error: any) {
      // Even if logout fails, clear local tokens
      console.error('[AuthService.logout] Logout error:', error)
    } finally {
      // Always clear local storage
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
    }
  }

  /**
   * Forgot password - Request password reset
   * POST /api/auth/forgot-password
   */
  async forgotPassword(email: string): Promise<{ message: string }> {
    try {
      const response = await http.post<{ message: string }>('/api/auth/forgot-password', {
        email
      })
      return response.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Password reset endpoint not available. Please contact support.')
      }
      throw this.handleError(error)
    }
  }

  /**
   * Reset password with token
   * POST /api/auth/reset-password
   */
  async resetPassword(newPassword: string, passwordVerification: string, token?: string): Promise<{ message: string }> {
    try {
      const response = await http.post<{ message: string }>('/api/auth/reset-password', {
        new_password: newPassword,
        password_verification: passwordVerification,
        token: token
      })
      return response.data
    } catch (error: any) {
      throw this.handleError(error)
    }
  }

  /**
   * Initiate Google OAuth
   * POST /api/auth/google/start
   * Backend uses Supabase for OAuth, returns redirect URL
   */
  async initiateGoogleOAuth(redirectTo?: string): Promise<string> {
    try {
      // Construct redirect URI - use current origin + /auth/callback
      const redirectUri = redirectTo || `${window.location.origin}/auth/callback`
      
      const response = await http.post<{ url: string; provider: string }>('/api/auth/google/start', {
        redirect_to: redirectUri
      })
      
      if (!response.data || !response.data.url) {
        throw new Error('Invalid response from server: missing OAuth URL')
      }
      
      return response.data.url
    } catch (error: any) {
      if (error.response?.status === 404 || error.response?.status === 405) {
        throw new Error('Google OAuth is not yet configured. Please use email and password to sign in.')
      }
      throw this.handleError(error)
    }
  }

  /**
   * Get current user info
   * GET /api/b2b/users/me
   */
  async getCurrentUser(): Promise<UserInfo> {
    console.log('[AuthService.getCurrentUser] Fetching current user')
    try {
      const response = await http.get<UserInfo>('/api/b2b/users/me')
      console.log('[AuthService.getCurrentUser] User fetched:', response.data.email)
      return response.data
    } catch (error: any) {
      console.error('[AuthService.getCurrentUser] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Health check
   * GET /api/auth/health
   */
  async healthCheck(): Promise<{ status: string; service: string }> {
    try {
      const response = await http.get<{ status: string; service: string }>('/api/auth/health')
      return response.data
    } catch (error: any) {
      throw this.handleError(error)
    }
  }

  /**
   * Error handler - Extracts meaningful error messages
   * Prioritizes specific error codes/patterns over generic backend messages
   */
  private handleError(error: any): Error {
    if (error.response) {
      // Server responded with error
      const status = error.response.status
      const data = error.response.data
      
      // Extract all possible error information from response
      const errorCode = data?.error || data?.code || data?.error_code
      const backendMessage = data?.message || data?.detail || data?.error
      const rawMessage = typeof data === 'string' && data.trim() ? data : null
      
      // Step 1: Check for specific error codes first (most reliable)
      const codeBasedMessage = this.getMessageFromErrorCode(errorCode, status)
      if (codeBasedMessage) {
        return this.createError(codeBasedMessage, status, data)
      }
      
      // Step 2: Check if backend message contains recognizable patterns
      const patternBasedMessage = this.getMessageFromPattern(backendMessage || rawMessage, status)
      if (patternBasedMessage) {
        return this.createError(patternBasedMessage, status, data)
      }
      
      // Step 3: Use status-based messages for common auth scenarios
      // These override generic backend messages for better UX
      const statusBasedMessage = this.getMessageFromStatus(status)
      if (statusBasedMessage) {
        return this.createError(statusBasedMessage, status, data)
      }
      
      // Step 4: Fall back to backend message if it seems specific enough
      if (backendMessage && !this.isGenericMessage(backendMessage)) {
        return this.createError(backendMessage, status, data)
      }
      
      // Step 5: Final fallback
      return this.createError(`An error occurred (${status}). Please try again.`, status, data)
      
    } else if (error.request) {
      // Request made but no response
      return new Error('Network error. Please check your connection and try again.')
    } else {
      // Something else happened
      return new Error(error.message || 'An unexpected error occurred')
    }
  }

  /**
   * Map specific error codes to user-friendly messages
   */
  private getMessageFromErrorCode(errorCode: string | undefined, status: number): string | null {
    if (!errorCode) return null
    
    const code = errorCode.toLowerCase()
    
    const errorCodeMessages: Record<string, string> = {
      // Authentication errors
      'invalid_credentials': 'Invalid email or password. Please check your credentials and try again.',
      'invalid_password': 'Invalid email or password. Please check your credentials and try again.',
      'wrong_password': 'Invalid email or password. Please check your credentials and try again.',
      'bad_credentials': 'Invalid email or password. Please check your credentials and try again.',
      'authentication_failed': 'Invalid email or password. Please check your credentials and try again.',
      
      // User errors
      'user_not_found': 'No account found with this email address. Please check your email or sign up.',
      'email_not_found': 'No account found with this email address. Please check your email or sign up.',
      'account_not_found': 'No account found with this email address. Please check your email or sign up.',
      'user_exists': 'This email is already registered. Please sign in instead.',
      'email_exists': 'This email is already registered. Please sign in instead.',
      'duplicate_email': 'This email is already registered. Please sign in instead.',
      
      // Account status errors
      'account_disabled': 'Your account has been disabled. Please contact support for assistance.',
      'account_locked': 'Your account has been temporarily locked. Please try again later or contact support.',
      'account_suspended': 'Your account has been suspended. Please contact support for assistance.',
      'email_not_verified': 'Please verify your email address before signing in. Check your inbox for the verification email.',
      'email_not_confirmed': 'Please verify your email address before signing in. Check your inbox for the verification email.',
      
      // Rate limiting (signup, confirmation email, forgot password, etc.)
      'rate_limited':
        'Too many sign-up or email attempts. Please wait a few minutes before trying again.',
      'too_many_requests': 'Too many attempts. Please wait a few minutes before trying again.',
      'throttled': 'Too many attempts. Please wait a few minutes before trying again.',
      
      // Password errors
      'weak_password': 'Password is too weak. Please use at least 8 characters with letters and numbers.',
      'password_too_short': 'Password must be at least 8 characters long.',
      'password_too_weak': 'Password is too weak. Please include uppercase, lowercase, and numbers.',
      'passwords_do_not_match': 'Passwords do not match. Please try again.',
      
      // Token errors
      'invalid_token': 'The link has expired or is invalid. Please request a new one.',
      'token_expired': 'The link has expired. Please request a new one.',
      'expired_token': 'The link has expired. Please request a new one.',
      
      // Server/config errors
      'configuration_error': 'Service temporarily unavailable. Please try again later.',
      'internal_error': 'Something went wrong on our end. Please try again later.',
    }
    
    return errorCodeMessages[code] || null
  }

  /**
   * Detect patterns in error messages and map to user-friendly versions
   */
  private getMessageFromPattern(message: string | null, status: number): string | null {
    if (!message) return null
    
    const lowerMessage = message.toLowerCase()
    
    // Invalid credentials patterns
    if (lowerMessage.includes('invalid credentials') || 
        lowerMessage.includes('invalid password') ||
        lowerMessage.includes('wrong password') ||
        lowerMessage.includes('incorrect password') ||
        lowerMessage.includes('password is incorrect') ||
        lowerMessage.includes('authentication failed')) {
      return 'Invalid email or password. Please check your credentials and try again.'
    }
    
    // User not found patterns
    if (lowerMessage.includes('user not found') || 
        lowerMessage.includes('no user') ||
        lowerMessage.includes('account not found') ||
        lowerMessage.includes('email not found')) {
      return 'No account found with this email address. Please check your email or sign up.'
    }
    
    // Email already exists patterns
    if (lowerMessage.includes('already registered') || 
        lowerMessage.includes('already exists') ||
        lowerMessage.includes('email exists') ||
        lowerMessage.includes('duplicate')) {
      return 'This email is already registered. Please sign in instead.'
    }
    
    // Email verification patterns
    if (lowerMessage.includes('not verified') || 
        lowerMessage.includes('not confirmed') ||
        lowerMessage.includes('verify your email')) {
      return 'Please verify your email address before signing in. Check your inbox for the verification email.'
    }
    
    // Rate limiting patterns (never surface Supabase dashboard / infra hints to users)
    if (lowerMessage.includes('too many') ||
        lowerMessage.includes('rate limit') ||
        lowerMessage.includes('try again later') ||
        lowerMessage.includes('raise limits')) {
      return 'Too many attempts. Please wait a few minutes before trying again.'
    }
    
    // Token/link expiration patterns
    if (lowerMessage.includes('expired') || 
        lowerMessage.includes('invalid token') ||
        lowerMessage.includes('invalid link')) {
      return 'The link has expired or is invalid. Please request a new one.'
    }
    
    return null
  }

  /**
   * Get user-friendly message based on HTTP status code
   */
  private getMessageFromStatus(status: number): string | null {
    switch (status) {
      case 400:
        // Generic bad request - could be validation error
        return null // Let other handlers deal with it or fall back to backend message
      case 401:
        return 'Invalid email or password. Please check your credentials and try again.'
      case 403:
        return 'Access denied. Please contact your administrator.'
      case 404:
        return 'The requested resource was not found.'
      case 409:
        return 'This email is already registered. Please sign in instead.'
      case 422:
        return 'Please check your input and try again.'
      case 429:
        return 'Too many attempts. Please wait a few minutes before trying again.'
      case 500:
        return 'Server error. Please try again later or contact support if the problem persists.'
      case 502:
      case 503:
      case 504:
        return 'Service temporarily unavailable. Please try again in a few moments.'
      default:
        return null
    }
  }

  /**
   * Check if a message is generic/unhelpful and should be overridden
   */
  private isGenericMessage(message: string): boolean {
    const lowerMessage = message.toLowerCase()
    
    const genericPatterns = [
      'an error occurred',
      'something went wrong',
      'please try again',
      'unknown error',
      'internal server error',
      'unexpected error',
      'error occurred during',
      'failed to process',
      'request failed',
      'operation failed',
    ]
    
    return genericPatterns.some(pattern => lowerMessage.includes(pattern))
  }

  /**
   * Create a custom error with status and data attached
   */
  private createError(message: string, status: number, data: any): Error {
    const customError = new Error(message)
    ;(customError as any).status = status
    ;(customError as any).data = data
    return customError
  }
}

export default new AuthService()
